import React, { useState, useEffect, useCallback } from 'react';
import { providerAdminAPI, adminAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { 
  Building2, Users, Plus, Edit2, Trash2, Loader2, 
  Check, X, Search, RefreshCw, MapPin, Phone, Mail,
  Calendar, DollarSign, Eye, Send, MessageSquare,
  ChevronLeft, ArrowRight, CreditCard, Percent,
  CalendarDays, Clock, User, CheckCircle
} from 'lucide-react';

export default function ServiceProvidersTab() {
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [tours, setTours] = useState([]);
  const [activeTab, setActiveTab] = useState('providers');
  
  // Provider dialog
  const [showProviderDialog, setShowProviderDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerForm, setProviderForm] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: '',
    password: '',
    commission_rate: 15,
  });
  const [savingProvider, setSavingProvider] = useState(false);
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  
  // Assign tour dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignTourId, setAssignTourId] = useState('');
  const [assignProviderId, setAssignProviderId] = useState('');

  // Provider Detail View
  const [viewingProvider, setViewingProvider] = useState(null);
  const [providerBookings, setProviderBookings] = useState(null);
  const [providerPayroll, setProviderPayroll] = useState(null);
  const [providerDetailTab, setProviderDetailTab] = useState('calendar');
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Payment dialog
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    notes: '',
  });
  const [recordingPayment, setRecordingPayment] = useState(false);
  
  // Commission edit dialog
  const [showCommissionDialog, setShowCommissionDialog] = useState(false);
  const [commissionForm, setCommissionForm] = useState({
    commission_type: 'percentage',
    commission_value: 15,
  });
  const [savingCommission, setSavingCommission] = useState(false);

  // Send booking list
  const [sendingList, setSendingList] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [providersRes, toursRes] = await Promise.all([
        providerAdminAPI.getAll(),
        adminAPI.getAllTours(),
      ]);
      setProviders(providersRes.data || []);
      setTours(toursRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadProviderDetail = useCallback(async (provider) => {
    setLoadingDetail(true);
    try {
      const [bookingsRes, payrollRes] = await Promise.all([
        providerAdminAPI.getProviderBookings(provider.id),
        providerAdminAPI.getProviderPayroll(provider.id),
      ]);
      setProviderBookings(bookingsRes.data);
      setProviderPayroll(payrollRes.data);
    } catch (error) {
      console.error('Error loading provider detail:', error);
      toast.error('Failed to load provider details');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const openProviderDetail = async (provider) => {
    setViewingProvider(provider);
    setProviderDetailTab('calendar');
    await loadProviderDetail(provider);
  };

  const saveProvider = async () => {
    if (!providerForm.name || !providerForm.email || !providerForm.phone) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!selectedProvider && !providerForm.password) {
      toast.error('Password is required for new providers');
      return;
    }
    
    setSavingProvider(true);
    try {
      if (selectedProvider) {
        await providerAdminAPI.update(selectedProvider.id, providerForm);
        toast.success('Provider updated');
      } else {
        await providerAdminAPI.create(providerForm);
        toast.success('Provider created');
      }
      setShowProviderDialog(false);
      setProviderForm({ name: '', email: '', phone: '', company_name: '', password: '', commission_rate: 15 });
      setSelectedProvider(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save provider');
    } finally {
      setSavingProvider(false);
    }
  };

  const deleteProvider = async (provider) => {
    if (!window.confirm(`Delete provider "${provider.name}"?`)) return;
    
    try {
      await providerAdminAPI.delete(provider.id);
      toast.success('Provider deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete provider');
    }
  };

  const approveProvider = async (provider) => {
    try {
      await providerAdminAPI.approve(provider.id);
      toast.success('Provider approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve provider');
    }
  };

  const assignTourToProvider = async () => {
    if (!assignTourId || !assignProviderId) {
      toast.error('Please select both tour and provider');
      return;
    }
    
    try {
      await providerAdminAPI.assignTour(assignTourId, assignProviderId);
      toast.success('Tour assigned to provider');
      setShowAssignDialog(false);
      setAssignTourId('');
      setAssignProviderId('');
      loadData();
    } catch (error) {
      toast.error('Failed to assign tour');
    }
  };

  const updateCommission = async () => {
    if (!viewingProvider) return;
    
    setSavingCommission(true);
    try {
      await providerAdminAPI.updateCommission(viewingProvider.id, commissionForm);
      toast.success('Commission rate updated');
      setShowCommissionDialog(false);
      loadData();
      loadProviderDetail(viewingProvider);
    } catch (error) {
      toast.error('Failed to update commission');
    } finally {
      setSavingCommission(false);
    }
  };

  const recordPayment = async () => {
    if (!viewingProvider || !paymentForm.amount) {
      toast.error('Please enter payment amount');
      return;
    }
    
    setRecordingPayment(true);
    try {
      await providerAdminAPI.recordPayment(viewingProvider.id, {
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes,
      });
      toast.success('Payment recorded');
      setShowPaymentDialog(false);
      setPaymentForm({ amount: '', payment_method: 'bank_transfer', notes: '' });
      loadProviderDetail(viewingProvider);
    } catch (error) {
      toast.error('Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };

  const sendBookingList = async (sendVia) => {
    if (!viewingProvider) return;
    
    setSendingList(true);
    try {
      const res = await providerAdminAPI.sendBookingList(viewingProvider.id, sendVia);
      toast.success(res.data.message || 'Booking list sent');
    } catch (error) {
      toast.error('Failed to send booking list');
    } finally {
      setSendingList(false);
    }
  };

  // Filter providers
  const filteredProviders = providers.filter(p => 
    !searchTerm || 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Separate pending and active providers
  const pendingProviders = filteredProviders.filter(p => !p.is_approved);
  const activeProviders = filteredProviders.filter(p => p.is_approved);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Provider Detail View
  if (viewingProvider) {
    return (
      <div className="space-y-6" data-testid="provider-detail-view">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => { setViewingProvider(null); setProviderBookings(null); setProviderPayroll(null); }}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Building2 className="h-6 w-6 text-blue-600" />
                {viewingProvider.name}
              </h2>
              <p className="text-muted-foreground">
                {viewingProvider.company_name && `${viewingProvider.company_name} • `}
                {viewingProvider.email}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setCommissionForm({
                  commission_type: providerBookings?.provider?.commission_type || 'percentage',
                  commission_value: providerBookings?.provider?.commission_rate || 15,
                });
                setShowCommissionDialog(true);
              }}
              className="gap-2"
            >
              <Percent className="h-4 w-4" />
              Commission: {providerBookings?.provider?.commission_rate || 15}%
            </Button>
            <Button 
              variant="default" 
              onClick={() => sendBookingList('email')}
              disabled={sendingList}
              className="gap-2"
            >
              {sendingList ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send Booking List
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-4">
          <Card className="p-4 bg-blue-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CalendarDays className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Upcoming</p>
                <p className="text-2xl font-bold text-blue-600">{providerBookings?.stats?.upcoming_bookings || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-green-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Earnings</p>
                <p className="text-2xl font-bold text-green-600">{formatPrice(providerBookings?.stats?.total_earnings)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-amber-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Earnings</p>
                <p className="text-2xl font-bold text-amber-600">{formatPrice(providerBookings?.stats?.pending_earnings)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-purple-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold text-purple-600">{formatPrice(providerBookings?.stats?.completed_earnings)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-red-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <CreditCard className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Balance Owed</p>
                <p className="text-2xl font-bold text-red-600">{formatPrice(providerPayroll?.summary?.balance_owed)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Sub-tabs */}
        <Tabs value={providerDetailTab} onValueChange={setProviderDetailTab} className="w-full">
          <TabsList className="grid grid-cols-4 w-fit">
            <TabsTrigger value="calendar" className="gap-2">
              <CalendarDays className="h-4 w-4" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="payroll" className="gap-2">
              <DollarSign className="h-4 w-4" />
              Payroll
            </TabsTrigger>
            <TabsTrigger value="communication" className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Communication
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <Edit2 className="h-4 w-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Calendar View */}
          <TabsContent value="calendar" className="mt-6">
            {loadingDetail ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="font-bold text-lg">Upcoming Bookings</h3>
                {Object.keys(providerBookings?.calendar?.upcoming || {}).length === 0 ? (
                  <Card className="p-8 text-center">
                    <CalendarDays className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No upcoming bookings assigned</p>
                  </Card>
                ) : (
                  Object.entries(providerBookings?.calendar?.upcoming || {}).map(([date, data]) => (
                    <Card key={date} className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Calendar className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-bold text-lg">{formatDate(date)}</p>
                            <p className="text-sm text-muted-foreground">
                              {data.bookings.length} booking{data.bookings.length !== 1 ? 's' : ''} • {data.total_guests} guest{data.total_guests !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-green-100 text-green-700 text-lg">
                          {formatPrice(data.total_earnings)}
                        </Badge>
                      </div>
                      <div className="space-y-2 pl-12">
                        {data.bookings.map((booking) => (
                          <div key={booking.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-4">
                              <div className="text-center min-w-[60px]">
                                <p className="font-bold">{booking.time || 'TBD'}</p>
                              </div>
                              <div>
                                <p className="font-semibold">{booking.customer_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {booking.guests} guest{booking.guests !== 1 ? 's' : ''} • {booking.tour_title}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Pickup: {booking.pickup || 'Not specified'}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">{formatPrice(booking.provider_earnings)}</p>
                              <Badge variant={booking.payment_status === 'paid' ? 'default' : 'secondary'} className="text-xs">
                                {booking.payment_status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))
                )}

                {/* Past Bookings */}
                {Object.keys(providerBookings?.calendar?.past || {}).length > 0 && (
                  <>
                    <h3 className="font-bold text-lg mt-8">Past Bookings</h3>
                    {Object.entries(providerBookings?.calendar?.past || {}).slice(0, 5).map(([date, data]) => (
                      <Card key={date} className="p-4 bg-muted/30">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-gray-100 rounded-lg">
                              <Calendar className="h-5 w-5 text-gray-500" />
                            </div>
                            <div>
                              <p className="font-medium">{formatDate(date)}</p>
                              <p className="text-sm text-muted-foreground">
                                {data.bookings.length} booking{data.bookings.length !== 1 ? 's' : ''} • {data.total_guests} guest{data.total_guests !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <Badge variant="outline">{formatPrice(data.total_earnings)}</Badge>
                        </div>
                      </Card>
                    ))}
                  </>
                )}
              </div>
            )}
          </TabsContent>

          {/* Payroll View */}
          <TabsContent value="payroll" className="mt-6">
            <div className="space-y-6">
              {/* Summary */}
              <Card className="p-6 bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-xl mb-2">Payroll Summary</h3>
                    <div className="grid grid-cols-3 gap-8">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Earned</p>
                        <p className="text-2xl font-bold text-green-600">{formatPrice(providerPayroll?.summary?.total_earned)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Total Paid</p>
                        <p className="text-2xl font-bold text-blue-600">{formatPrice(providerPayroll?.summary?.total_paid)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Balance Owed</p>
                        <p className="text-2xl font-bold text-red-600">{formatPrice(providerPayroll?.summary?.balance_owed)}</p>
                      </div>
                    </div>
                  </div>
                  <Button onClick={() => setShowPaymentDialog(true)} className="gap-2 bg-green-600 hover:bg-green-700">
                    <CreditCard className="h-4 w-4" />
                    Record Payment
                  </Button>
                </div>
              </Card>

              {/* Earnings Breakdown */}
              <Card className="p-4">
                <h4 className="font-bold mb-4">Earnings Breakdown</h4>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-semibold">Date</th>
                        <th className="text-left p-3 font-semibold">Tour</th>
                        <th className="text-left p-3 font-semibold">Customer</th>
                        <th className="text-left p-3 font-semibold">Guests</th>
                        <th className="text-left p-3 font-semibold">Booking Total</th>
                        <th className="text-left p-3 font-semibold">Provider Earnings</th>
                        <th className="text-left p-3 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(providerPayroll?.breakdown || []).map((item) => (
                        <tr key={item.booking_id} className="border-t">
                          <td className="p-3">{formatDate(item.date)}</td>
                          <td className="p-3">{item.tour_title}</td>
                          <td className="p-3">{item.customer_name}</td>
                          <td className="p-3">{item.guests}</td>
                          <td className="p-3">{formatPrice(item.booking_total)}</td>
                          <td className="p-3 font-bold text-green-600">{formatPrice(item.provider_earnings)}</td>
                          <td className="p-3">
                            <Badge variant={item.payment_status === 'paid' ? 'default' : 'secondary'}>
                              {item.payment_status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Payment History */}
              <Card className="p-4">
                <h4 className="font-bold mb-4">Payment History</h4>
                {(providerPayroll?.payment_history || []).length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">No payments recorded yet</p>
                ) : (
                  <div className="space-y-2">
                    {providerPayroll.payment_history.map((payment) => (
                      <div key={payment.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div>
                          <p className="font-semibold">{formatPrice(payment.amount)}</p>
                          <p className="text-sm text-muted-foreground">
                            {payment.payment_method} • {new Date(payment.created_at).toLocaleDateString()}
                          </p>
                          {payment.notes && <p className="text-xs text-muted-foreground mt-1">{payment.notes}</p>}
                        </div>
                        <Badge className="bg-green-500">Paid</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Communication */}
          <TabsContent value="communication" className="mt-6">
            <Card className="p-6">
              <h3 className="font-bold text-lg mb-4">Contact Provider</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">Email</p>
                  <p className="font-semibold">{viewingProvider.email}</p>
                  <Button 
                    variant="outline" 
                    className="mt-3 gap-2"
                    onClick={() => sendBookingList('email')}
                    disabled={sendingList}
                  >
                    {sendingList ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                    Send Booking List via Email
                  </Button>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2">Phone / WhatsApp</p>
                  <p className="font-semibold">{viewingProvider.phone}</p>
                  <Button 
                    variant="outline" 
                    className="mt-3 gap-2"
                    onClick={() => sendBookingList('whatsapp')}
                    disabled={sendingList}
                  >
                    {sendingList ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
                    Send via WhatsApp
                  </Button>
                </div>
              </div>
            </Card>
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings" className="mt-6">
            <Card className="p-6">
              <h3 className="font-bold text-lg mb-4">Provider Settings</h3>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Name</Label>
                    <p className="font-semibold text-lg">{viewingProvider.name}</p>
                  </div>
                  <div>
                    <Label>Company</Label>
                    <p className="font-semibold text-lg">{viewingProvider.company_name || 'N/A'}</p>
                  </div>
                  <div>
                    <Label>Email</Label>
                    <p className="font-semibold">{viewingProvider.email}</p>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <p className="font-semibold">{viewingProvider.phone}</p>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <Label>Commission Rate</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <p className="font-semibold text-2xl text-purple-600">
                      {providerBookings?.provider?.commission_rate || 15}%
                    </p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setCommissionForm({
                          commission_type: 'percentage',
                          commission_value: providerBookings?.provider?.commission_rate || 15,
                        });
                        setShowCommissionDialog(true);
                      }}
                    >
                      <Edit2 className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    This percentage goes to Tour Finder. Provider receives {100 - (providerBookings?.provider?.commission_rate || 15)}% of booking value.
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Commission Dialog */}
        <Dialog open={showCommissionDialog} onOpenChange={setShowCommissionDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Commission Rate</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Commission Rate (%)</Label>
                <Input
                  type="number"
                  value={commissionForm.commission_value}
                  onChange={(e) => setCommissionForm({ ...commissionForm, commission_value: parseFloat(e.target.value) || 0 })}
                  min={0}
                  max={100}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Tour Finder keeps {commissionForm.commission_value}%, provider gets {100 - commissionForm.commission_value}%
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCommissionDialog(false)}>Cancel</Button>
              <Button onClick={updateCommission} disabled={savingCommission}>
                {savingCommission && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Payment to {viewingProvider?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="zelle">Zelle</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="paypal">PayPal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  placeholder="Payment notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
              <Button onClick={recordPayment} disabled={recordingPayment} className="bg-green-600 hover:bg-green-700">
                {recordingPayment && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Record Payment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main Provider List View
  return (
    <div className="space-y-6" data-testid="service-providers-tab">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Service Providers</h2>
          <p className="text-muted-foreground">Manage tour operators and service providers</p>
        </div>
        <Button onClick={() => { setSelectedProvider(null); setProviderForm({ name: '', email: '', phone: '', company_name: '', password: '', commission_rate: 15 }); setShowProviderDialog(true); }} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Provider
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Providers</p>
              <p className="text-2xl font-bold">{providers.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-2xl font-bold">{activeProviders.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Calendar className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pending Approval</p>
              <p className="text-2xl font-bold">{pendingProviders.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <MapPin className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Tours</p>
              <p className="text-2xl font-bold">{providers.reduce((sum, p) => sum + (p.tour_count || 0), 0)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-3 w-fit">
          <TabsTrigger value="providers" className="gap-2">
            <Building2 className="h-4 w-4" />
            All Providers
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-2">
            <Calendar className="h-4 w-4" />
            Pending
            {pendingProviders.length > 0 && (
              <Badge variant="destructive" className="ml-1">{pendingProviders.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="tours" className="gap-2">
            <MapPin className="h-4 w-4" />
            Tour Assignments
          </TabsTrigger>
        </TabsList>

        {/* All Providers */}
        <TabsContent value="providers" className="mt-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search providers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button variant="outline" onClick={loadData} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>

            {activeProviders.length === 0 ? (
              <Card className="p-8 text-center">
                <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No active providers</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {activeProviders.map((provider) => (
                  <Card 
                    key={provider.id} 
                    className="p-4 cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => openProviderDetail(provider)}
                    data-testid={`provider-card-${provider.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-lg">{provider.name}</span>
                            {provider.company_name && (
                              <Badge variant="secondary" className="text-xs">{provider.company_name}</Badge>
                            )}
                            {!provider.is_active && (
                              <Badge variant="destructive" className="text-xs">Inactive</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {provider.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {provider.phone}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {provider.tour_count || 0} tours
                            </span>
                            <span className="flex items-center gap-1">
                              <Percent className="h-3 w-3" />
                              {provider.commission_rate || 15}% commission
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="default" 
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); openProviderDetail(provider); }}
                          className="gap-1"
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={(e) => { 
                            e.stopPropagation();
                            setSelectedProvider(provider); 
                            setProviderForm({ 
                              name: provider.name, 
                              email: provider.email, 
                              phone: provider.phone, 
                              company_name: provider.company_name || '',
                              password: '',
                              commission_rate: provider.commission_rate || 15
                            }); 
                            setShowProviderDialog(true); 
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteProvider(provider); }} className="text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Pending Approvals */}
        <TabsContent value="pending" className="mt-6">
          {pendingProviders.length === 0 ? (
            <Card className="p-8 text-center">
              <Check className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <p className="text-muted-foreground">No pending applications</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingProviders.map((provider) => (
                <Card key={provider.id} className="p-4 border-amber-200 bg-amber-50/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{provider.name}</span>
                        {provider.company_name && (
                          <span className="text-muted-foreground">({provider.company_name})</span>
                        )}
                        <Badge variant="secondary" className="text-xs">Pending</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                        <span>{provider.email}</span>
                        <span>{provider.phone}</span>
                        <span>Applied: {new Date(provider.created_at).toLocaleDateString()}</span>
                      </div>
                      {provider.services_offered && (
                        <p className="text-sm mt-2 text-muted-foreground">{provider.services_offered}</p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => approveProvider(provider)} className="gap-1 text-green-600">
                        <Check className="h-4 w-4" />
                        Approve
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteProvider(provider)} className="text-red-600">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tour Assignments */}
        <TabsContent value="tours" className="mt-6">
          <Card className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="font-semibold text-lg">Tour Assignments</h3>
                <p className="text-sm text-muted-foreground">Assign tours to service providers</p>
              </div>
              <Button onClick={() => setShowAssignDialog(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Assign Tour
              </Button>
            </div>
            
            <div className="space-y-3">
              {tours.filter(t => t.service_provider_id).map((tour) => {
                const provider = providers.find(p => p.id === tour.service_provider_id);
                return (
                  <div key={tour.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-4">
                      {tour.images?.[0] && (
                        <img src={tour.images[0]} alt="" className="w-12 h-12 rounded object-cover" />
                      )}
                      <div>
                        <p className="font-medium">{tour.title}</p>
                        <p className="text-sm text-muted-foreground">${tour.price}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="gap-1">
                        <Building2 className="h-3 w-3" />
                        {provider?.name || 'Unknown'}
                      </Badge>
                    </div>
                  </div>
                );
              })}
              
              {tours.filter(t => !t.service_provider_id).length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3 text-muted-foreground">Unassigned Tours</h4>
                  {tours.filter(t => !t.service_provider_id).map((tour) => (
                    <div key={tour.id} className="flex items-center justify-between p-3 bg-gray-100 rounded-lg mb-2">
                      <div className="flex items-center gap-4">
                        {tour.images?.[0] && (
                          <img src={tour.images[0]} alt="" className="w-12 h-12 rounded object-cover" />
                        )}
                        <div>
                          <p className="font-medium">{tour.title}</p>
                          <p className="text-sm text-muted-foreground">${tour.price}</p>
                        </div>
                      </div>
                      <Badge variant="outline">Unassigned</Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Provider Dialog */}
      <Dialog open={showProviderDialog} onOpenChange={setShowProviderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedProvider ? 'Edit Provider' : 'Create Service Provider'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={providerForm.name}
                onChange={(e) => setProviderForm({ ...providerForm, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label>Company Name</Label>
              <Input
                value={providerForm.company_name}
                onChange={(e) => setProviderForm({ ...providerForm, company_name: e.target.value })}
                placeholder="Tour Company LLC"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={providerForm.email}
                onChange={(e) => setProviderForm({ ...providerForm, email: e.target.value })}
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input
                value={providerForm.phone}
                onChange={(e) => setProviderForm({ ...providerForm, phone: e.target.value })}
                placeholder="+1 787 555 1234"
              />
            </div>
            <div>
              <Label>Commission Rate (%)</Label>
              <Input
                type="number"
                value={providerForm.commission_rate}
                onChange={(e) => setProviderForm({ ...providerForm, commission_rate: parseFloat(e.target.value) || 15 })}
                min={0}
                max={100}
              />
              <p className="text-xs text-muted-foreground mt-1">Tour Finder keeps this %, provider gets the rest</p>
            </div>
            {!selectedProvider && (
              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={providerForm.password}
                  onChange={(e) => setProviderForm({ ...providerForm, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProviderDialog(false)}>Cancel</Button>
            <Button onClick={saveProvider} disabled={savingProvider}>
              {savingProvider && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {selectedProvider ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Tour Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Tour to Provider</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Select Tour</Label>
              <Select value={assignTourId} onValueChange={setAssignTourId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a tour" />
                </SelectTrigger>
                <SelectContent>
                  {tours.map((tour) => (
                    <SelectItem key={tour.id} value={tour.id}>
                      {tour.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Select Provider</Label>
              <Select value={assignProviderId} onValueChange={setAssignProviderId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a provider" />
                </SelectTrigger>
                <SelectContent>
                  {activeProviders.map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      {provider.name} {provider.company_name && `(${provider.company_name})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button onClick={assignTourToProvider}>Assign Tour</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
